/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import axios from "axios"
import { APIResponse } from "../../interfaces/api"
import { GetAPIError } from "./getResponseError"
import { authStorageAdapter } from "../auth/storageAdapter"
import { createElectronIpcAdapter } from "./electronAdapter"

// CONFIG
const timeout = 20000 // Было 10 сек, поменял на 20, так как создание отчёта за день, требует много времени.
const withCredentials = true

// Глобальная настройка axios для гарантированной передачи cookies (ory_kratos_session)
axios.defaults.withCredentials = true

export const API = axios.create({
  baseURL: process.env.NEXT_PUBLIC_SALES_API || undefined,
  timeout,
  withCredentials,
})

// Custom Axios Adapter via Electron IPC when running in Electron
API.defaults.adapter = createElectronIpcAdapter(API)

// Request Interceptor: подстановка Bearer-токена сессии Kratos только при наличии ory_st_
API.interceptors.request.use(
  async (config) => {
    config.withCredentials = true

    const token = await authStorageAdapter.getToken()
    // В Cookie-flow аутентификация в браузере выполняется через HttpOnly Cookie ory_kratos_session.
    // Заголовок Authorization: Bearer передается исключительно при наличии сессионного токена Kratos (ory_st_...).
    if (
      token &&
      typeof token === "string" &&
      token.startsWith("ory_st_") &&
      config.headers
    ) {
      if (typeof config.headers.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`)
      } else {
        ;(config.headers as Record<string, any>).Authorization =
          `Bearer ${token}`
      }
    } else if (config.headers) {
      // Удаляем устаревший/невалидный UUID-токен
      if (typeof config.headers.delete === "function") {
        config.headers.delete("Authorization")
      } else {
        delete (config.headers as Record<string, any>).Authorization
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response Interceptor: перехват 401 Unauthorized от Oathkeeper
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      console.warn(
        "[API Client]: 401 Unauthorized from Oathkeeper, resetting session"
      )
      await authStorageAdapter.clearToken()
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:session-expired"))
      }
    }
    return Promise.reject(error)
  }
)

export async function get<T>(url): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.get<APIResponse<T>>(url))
  } catch (err) {
    return axiosCatch(err)
  }
}

export async function post<T>(url, data?): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.post<APIResponse<T>>(url, data))
  } catch (err) {
    return axiosCatch(err)
  }
}

export async function login<T>(url, data): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.post<APIResponse<T>>(url, data))
  } catch (err) {
    return axiosCatch(err)
  }
}

export async function put<T>(url, data?): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.put<APIResponse<T>>(url, data))
  } catch (err) {
    return axiosCatch(err)
  }
}

export async function patch<T>(url, data?): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.patch<APIResponse<T>>(url, data))
  } catch (err) {
    return axiosCatch(err)
  }
}

export async function _delete<T>(url): Promise<APIResponse<T>> {
  try {
    return axiosThen(await API.delete<APIResponse<T>>(url))
  } catch (err) {
    return axiosCatch(err)
  }
}

export function axiosThen<T>(response): APIResponse<T> {
  if (process.env.NODE_ENV === "development") {
    console.log("axiosThen", response)
  }

  if (response.status != 200 || !response?.data?.success) {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "response.status != 200 || !response?.data?.success",
        response.status != 200 || !response?.data?.success
      )
    }

    return axiosCatch(response)
  }

  return response.data as APIResponse<T>
}

export function axiosCatch(err): APIResponse {
  const apiErr = GetAPIError(err?.response || err)

  if (process.env.NODE_ENV === "development") {
    console.log("axiosCatch", apiErr)
  }

  return apiErr
}
